import { useLayoutEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { isCreditNote } from "@fiscalo/shared-types";
import { format, endOfMonth, startOfMonth } from "date-fns";
import { ChevronDown, ChevronUp, Download, FileText, Info, Loader2, Trash2, Upload } from "lucide-react";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { UploadBoletoForm } from "@/components/shared/UploadBoletoForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getTaxLabelKey,
  getAuthorityLabelKey,
  formatPeriodForDisplay,
} from "@/lib/fiscalAgendaDisplay";
import { getIvaHistory } from "@/services/dashboardService";
import {
  getTenantAccountantRequest,
  markBoletoInProgress,
  uploadBoleto,
  removeBoleto,
} from "@/services/fiscalService";
import { listInvoices, listRecibidas, type RecibidaItem } from "@/services/invoiceService";
import { uploadTenantDocument } from "@/services/authService";
import { setViewingTenantId } from "@/services/apiClient";
import { cn } from "@/lib/utils";
import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";

const ISO_DATE = "yyyy-MM-dd";

/** DGI rounding: decimal < 0.05 round down, ≥ 0.05 round up. */
function roundDgi(value: number): number {
  const abs = Math.abs(value);
  const sign = value < 0 ? -1 : 1;
  const floor = Math.floor(abs);
  const frac = abs - floor;
  const rounded = frac < 0.05 ? floor : Math.ceil(abs);
  return sign * rounded;
}

const FROM_YEAR = new Date().getFullYear() - 1;
const TO_YEAR = new Date().getFullYear() + 1;

export function SolicitudDetailPage(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: requestId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [ivaDetailExpanded, setIvaDetailExpanded] = useState(false);

  // Accountant-side opens of this page include ?tenant=<clientTenantId>
  // so all tenant-scoped API calls below resolve against the client's
  // company without swapping the persistent active tenant. apiClient
  // reads this and stamps X-Tenant-Id on every request; backend
  // AuthGuard validates membership and uses the override for THAT
  // request only. useLayoutEffect (not useEffect) so it runs before
  // React Query's first fetch fires.
  const viewingTenant = searchParams.get("tenant");
  useLayoutEffect(() => {
    setViewingTenantId(viewingTenant);
    return () => {
      setViewingTenantId(null);
    };
  }, [viewingTenant]);

  const from = `${FROM_YEAR}-01`;
  const to = `${TO_YEAR}-12`;

  const { user } = useAuth();

  // Same paid gate as the list page: this endpoint calls assertPaidPlan and
  // 403s for a free tenant, and PlanGateOverlay still mounts its children, so
  // the route gate alone would leave the request firing behind the blur.
  const { data: planTenant, isFetched: planFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const hasPaidPlan = planFetched && isProOrUltraPlan(planTenant?.planId);

  const { data: request, isLoading: requestLoading } = useQuery({
    queryKey: ["tenant-accountant-request", requestId],
    queryFn: () => getTenantAccountantRequest(requestId!),
    enabled: Boolean(requestId) && hasPaidPlan,
  });

  const obligation = request?.fiscalObligation ?? null;

  const { data: ivaHistory = [], isLoading: ivaHistoryLoading } = useQuery({
    queryKey: ["estado-fiscal", "iva-history", from, to],
    queryFn: () => getIvaHistory(from, to),
    enabled: Boolean(obligation?.period),
  });

  /** IVA history rows are keyed by invoice (calculation) month; obligation.period is the declaration/due month. */
  const calculationPeriod = useMemo(() => {
    if (!obligation?.period) return null;
    const [y, m] = obligation.period.split("-").map(Number);
    if (!m || m < 1) return null;
    const calcMonth = m <= 1 ? 12 : m - 1;
    const calcYear = m <= 1 ? y - 1 : y;
    return `${calcYear}-${String(calcMonth).padStart(2, "0")}`;
  }, [obligation?.period]);

  const ivaRow = useMemo(() => {
    if (!calculationPeriod) return null;
    return ivaHistory.find((row) => row.period === calculationPeriod) ?? null;
  }, [calculationPeriod, ivaHistory]);

  const isIvaDgi = obligation?.taxType === "IVA" && obligation?.authority === "DGI";
  const showIvaSection = isIvaDgi;

  const [periodYear, periodMonth] = isIvaDgi && obligation?.period
    ? obligation.period.split("-").map(Number)
    : [0, 0];
  const calculationMonthStart = useMemo(() => {
    if (!isIvaDgi || !obligation?.period || !periodMonth) return null;
    return periodMonth <= 1
      ? new Date(periodYear - 1, 11, 1)
      : new Date(periodYear, periodMonth - 2, 1);
  }, [isIvaDgi, obligation?.period, periodYear, periodMonth]);
  const prevMonthStart = calculationMonthStart
    ? format(startOfMonth(calculationMonthStart), ISO_DATE)
    : "";
  const prevMonthEnd = calculationMonthStart
    ? format(endOfMonth(calculationMonthStart), ISO_DATE)
    : "";

  const { data: emitidasData, isLoading: emitidasLoading } = useQuery({
    queryKey: ["solicitud-detail", "iva-detail", "emitidas", prevMonthStart, prevMonthEnd],
    queryFn: () =>
      listInvoices({
        period: "custom",
        dateFrom: prevMonthStart,
        dateTo: prevMonthEnd,
        status: "accepted",
        limit: 100,
      }),
    enabled: ivaDetailExpanded && Boolean(prevMonthStart) && Boolean(prevMonthEnd),
  });

  const { data: recibidasData, isLoading: recibidasLoading } = useQuery({
    queryKey: ["solicitud-detail", "iva-detail", "recibidas", prevMonthStart, prevMonthEnd],
    queryFn: () =>
      listRecibidas({
        dateFrom: prevMonthStart,
        dateTo: prevMonthEnd,
        limit: 100,
        forIvaCompras: true,
      }),
    enabled: ivaDetailExpanded && Boolean(prevMonthStart) && Boolean(prevMonthEnd),
  });

  const periodTitle = obligation
    ? formatPeriodForDisplay(obligation.period, i18n.language)
    : "";
  const lastMonthDate = periodMonth <= 1
    ? new Date(periodYear - 1, 11, 1)
    : new Date(periodYear, periodMonth - 2, 1);
  const lastMonthName = lastMonthDate.toLocaleDateString(i18n.language, {
    month: "long",
  });

  const markInProgressMutation = useMutation({
    mutationFn: (obligationId: string) => markBoletoInProgress(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-accountant-requests"] });
      queryClient.invalidateQueries({
        queryKey: ["tenant-accountant-request", requestId],
      });
      toast.success(t("solicitudes.markInProgressSuccess"));
    },
    onError: () => toast.error(t("solicitudes.markInProgressError")),
  });

  const uploadBoletoMutation = useMutation({
    mutationFn: async (args: {
      obligationId: string;
      boletoNumber: string;
      file: File;
    }) => {
      const doc = await uploadTenantDocument(args.file);
      return uploadBoleto(args.obligationId, {
        boletoNumber: args.boletoNumber,
        boletoFileUrl: doc.fileUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-accountant-requests"] });
      queryClient.invalidateQueries({
        queryKey: ["tenant-accountant-request", requestId],
      });
      toast.success(t("estadoFiscal.boletoUploaded"));
      setUploadDialogOpen(false);
      navigate("/requests");
    },
    onError: () => toast.error(t("settings.fiscalProfile.error")),
  });

  const removeBoletoMutation = useMutation({
    mutationFn: (obligationId: string) => removeBoleto(obligationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-accountant-requests"] });
      queryClient.invalidateQueries({
        queryKey: ["tenant-accountant-request", requestId],
      });
      toast.success(t("estadoFiscal.boletoRemoved"));
      setRemoveDialogOpen(false);
    },
    onError: () => toast.error(t("settings.fiscalProfile.error")),
  });

  if (requestLoading) return <LoadingPage />;
  if (!requestId) {
    navigate("/requests", { replace: true });
    return null;
  }
  if (!request || !obligation) {
    navigate("/requests", { replace: true });
    return null;
  }

  const detailTitle = t("solicitudes.detailTitleFull", {
    type: t("solicitudes.typePaymentSlip"),
    tax: t(getTaxLabelKey(obligation.taxType)),
    authority: t(getAuthorityLabelKey(obligation.authority)),
    period: periodTitle,
  });

  return (
    <div className="fx-content">
      <PageHeader
        title={detailTitle}
        onBack={() => navigate(-1)}
      />
      <div className="mt-4 space-y-4">
        <Card
          className={cn(
            "border-border bg-card shadow-sm transition-shadow",
            obligation.status === "overdue" &&
              "border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/20",
            obligation.status === "due_today" &&
              "border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20"
          )}
        >
          <CardHeader className="pb-2">
            <div className="flex flex-row flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <FileText className="h-4 w-4" />
                  {t(getTaxLabelKey(obligation.taxType))} (
                  {t(getAuthorityLabelKey(obligation.authority))})
                </div>
                <p className="pt-1 text-xs text-muted-foreground">
                  {obligation.taxType === "IVA" && obligation.authority === "DGI"
                    ? t("estadoFiscal.ivaPeriodExplanation", {
                        month: lastMonthName,
                      })
                    : t("solicitudes.obligationDue", {
                        period: periodTitle,
                      })}
                </p>
                {obligation.dueDate && (
                  <div className="mt-2">
                    <Badge variant="secondary" className="inline-flex font-medium">
                      {t("estadoFiscal.dueDateLabel")}:{" "}
                      {formatDisplayDate(obligation.dueDate, {
                        useCalendarDate: true,
                        locale: i18n.language,
                      })}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {showIvaSection && (() => {
              if (ivaHistoryLoading) {
                return (
                  <div className="rounded-md border border-border bg-card p-6 shadow-sm">
                    <p className="text-sm font-medium text-foreground mb-2">
                      {t("estadoFiscal.ivaSummaryTitle")}
                    </p>
                    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t("solicitudes.ivaLoading")}
                    </div>
                  </div>
                );
              }
              const ivaDebited = ivaRow?.ivaDebitedUyu ?? 0;
              const ivaCredited = ivaRow?.ivaCreditedUyu ?? 0;
              const ivaAPagar = ivaRow?.ivaAPagarUyu ?? 0;
              const ivaDgiTotal = roundDgi(ivaAPagar);
              const hasData = ivaRow != null;
              return (
                  <>
                    <div className="rounded-md border border-border bg-card p-4 space-y-3 shadow-sm">
                      <p className="text-sm font-medium text-foreground">
                        {t("estadoFiscal.ivaSummaryTitle")}
                      </p>
                      {!hasData && (
                        <p className="text-sm text-muted-foreground">
                          {t("solicitudes.ivaNoDataForPeriod")}
                        </p>
                      )}
                      <div className="grid gap-2 sm:grid-cols-1 md:grid-cols-4">
                        <div>
                          <p className="text-xs text-foreground/80">
                            {t("estadoFiscal.ivaVentas")}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-foreground">
                            {hasData ? `${formatDashboardCurrency(ivaDebited)} UYU` : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-foreground/80">
                            {t("estadoFiscal.ivaCompras")}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-foreground">
                            {hasData ? `− ${formatDashboardCurrency(ivaCredited)} UYU` : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-foreground/80">
                            {t("estadoFiscal.ivaAPagar")}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-foreground">
                            {hasData
                              ? (ivaAPagar > 0
                                ? `${formatDashboardCurrency(ivaAPagar)} UYU`
                                : ivaAPagar < 0
                                  ? `− ${formatDashboardCurrency(-ivaAPagar)} UYU`
                                  : "0 UYU")
                              : "—"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-foreground/80 flex items-center justify-end gap-1">
                            {t("estadoFiscal.ivaDgiTotal")}
                            {hasData && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    aria-label={t("estadoFiscal.ivaDgiRoundingTooltip")}
                                  >
                                    <Info className="h-3.5 w-3.5" aria-hidden />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px]">
                                  <p>{t("estadoFiscal.ivaDgiRoundingTooltip")}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </p>
                          <p className="text-lg font-semibold tabular-nums text-foreground">
                            {hasData
                              ? (ivaDgiTotal > 0
                                ? `${formatDashboardCurrency(ivaDgiTotal)} UYU`
                                : ivaDgiTotal < 0
                                  ? `− ${formatDashboardCurrency(-ivaDgiTotal)} UYU`
                                  : "0 UYU")
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                    {hasData && (
                      <>
                    <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIvaDetailExpanded((v) => !v)}
                        className="gap-2 border-border font-medium text-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {ivaDetailExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4" />
                            {t("estadoFiscal.ivaDetailCollapse")}
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            {t("estadoFiscal.ivaDetailExpand")}
                          </>
                        )}
                      </Button>
                    </div>
                    {ivaDetailExpanded && (
                      <div className="space-y-6 border-t pt-4">
                        <div>
                          <p className="mb-2 text-sm font-medium text-foreground">
                            {t("estadoFiscal.ivaDetailEmitidas")}
                          </p>
                          {emitidasLoading ? (
                            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {t("estadoFiscal.ivaDetailLoading")}
                            </div>
                          ) : emitidasData?.data?.length ? (
                            <div className="overflow-x-auto rounded-md border border-border bg-card">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-b border-border bg-muted/70 hover:bg-muted/70">
                                    <TableHead className="font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailDate")}
                                    </TableHead>
                                    <TableHead className="font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailDoc")}
                                    </TableHead>
                                    <TableHead className="text-right font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailTotal")}
                                    </TableHead>
                                    <TableHead className="text-right font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailIva")}
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {emitidasData.data.map((inv) => {
                                    const total = inv.totals?.total?.amount ?? 0;
                                    const iva = inv.totals?.taxTotal?.amount ?? 0;
                                    const cur = (inv.totals?.total?.currency ?? inv.currency ?? "UYU").toUpperCase();
                                    const docLabel =
                                      inv.cfeType === "101"
                                        ? t("estadoFiscal.ivaDetailETicket", "e-Ticket")
                                        : inv.cfeType === "111"
                                          ? t("estadoFiscal.ivaDetailEFactura", "e-Factura")
                                          : inv.cfeType === "121"
                                            ? t("estadoFiscal.ivaDetailEExport", "e-Factura Exportación")
                                            : inv.cfeType;
                                    return (
                                      <TableRow
                                        key={inv.id}
                                        className="cursor-pointer border-b border-border transition-colors hover:bg-muted/50"
                                        onClick={() => navigate(`/invoices/${inv.id}`)}
                                      >
                                        <TableCell className="text-foreground">
                                          {formatDisplayDate(inv.issueDate ?? "", {
                                            locale: i18n.language,
                                            useCalendarDate: true,
                                          })}
                                        </TableCell>
                                        <TableCell className="text-foreground">
                                          {docLabel} {inv.internalNumber ?? inv.id.slice(0, 8)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-foreground">
                                          {formatDashboardCurrency(total)} {cur}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-foreground">
                                          {formatDashboardCurrency(iva)} {cur}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="py-3 text-sm text-muted-foreground">
                              {t("estadoFiscal.ivaDetailNoEmitidas")}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="mb-2 text-sm font-medium text-foreground">
                            {t("estadoFiscal.ivaDetailRecibidas")}
                          </p>
                          {recibidasLoading ? (
                            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              {t("estadoFiscal.ivaDetailLoading")}
                            </div>
                          ) : recibidasData?.data?.length ? (
                            <div className="overflow-x-auto rounded-md border border-border bg-card">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-b border-border bg-muted/70 hover:bg-muted/70">
                                    <TableHead className="font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailDate")}
                                    </TableHead>
                                    <TableHead className="font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailEmitter")}
                                    </TableHead>
                                    <TableHead className="font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailDoc")}
                                    </TableHead>
                                    <TableHead className="text-right font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailTotal")}
                                    </TableHead>
                                    <TableHead className="text-right font-semibold text-foreground">
                                      {t("estadoFiscal.ivaDetailIva")}
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(recibidasData.data as RecibidaItem[]).map((r) => (
                                    <TableRow key={r.id} className="border-b border-border transition-colors hover:bg-muted/50">
                                      <TableCell className="text-foreground">
                                        {formatDisplayDate(r.fechaHora?.slice(0, 10) ?? "")}
                                      </TableCell>
                                      <TableCell className="text-foreground">
                                        <div className="flex flex-col">
                                          <span className="font-medium">
                                            {r.legalName?.trim() || r.rucEmisor?.trim() || "—"}
                                          </span>
                                          {r.legalName?.trim() && r.rucEmisor?.trim() && (
                                            <span className="text-xs text-foreground/80 tabular-nums">
                                              {r.rucEmisor.trim()}
                                            </span>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-foreground">
                                        {t(`invoices.cfeTypes.${r.tipoCFE}`, r.tipoCFE)} / {t("recibidas.serieLabel", "Serie")} {r.serie} {r.nro}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums text-foreground">
                                        {formatDashboardCurrency(
                                          isCreditNote(r.tipoCFE) ? -Number(r.montoTotal) : Number(r.montoTotal)
                                        )}{" "}
                                        {r.moneda}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums text-foreground">
                                        {formatDashboardCurrency(
                                          isCreditNote(r.tipoCFE) ? -Number(r.totalIVA) : Number(r.totalIVA)
                                        )}{" "}
                                        {r.moneda}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="py-3 text-sm text-muted-foreground">
                              {t("estadoFiscal.ivaDetailNoRecibidas")}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    <p className="text-sm text-foreground/80">
                      {t("estadoFiscal.ivaPaymentNote")}
                    </p>
                      </>
                    )}
                  </>
                );
              })()}
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {(() => {
                const hasBoleto = Boolean(
                  obligation.boletoFileUrl && obligation.boletoUploadedAt
                );
                if (hasBoleto) {
                  const actions: TableAction[] = [
                    {
                      label: t("fiscalAgenda.downloadDocument"),
                      icon: Download,
                      onClick: () =>
                        window.open(obligation.boletoFileUrl ?? "", "_blank"),
                    },
                    {
                      label: t("fiscalAgenda.replaceDocument"),
                      icon: Upload,
                      onClick: () => setUploadDialogOpen(true),
                    },
                    {
                      label: t("fiscalAgenda.removeDocument"),
                      icon: Trash2,
                      onClick: () => setRemoveDialogOpen(true),
                      variant: "destructive",
                    },
                  ];
                  return (
                    <TableActionsMenu
                      actions={actions}
                      triggerLabel={t("fiscalAgenda.actions")}
                    />
                  );
                }
                if (obligation.boletoInProgressAt != null) {
                  return (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={() => setUploadDialogOpen(true)}
                      className="gap-1.5 transition-colors duration-150"
                    >
                      <Upload className="h-4 w-4" />
                      {t("solicitudes.uploadDocument")}
                    </Button>
                  );
                }
                return (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => markInProgressMutation.mutate(obligation.id)}
                    disabled={markInProgressMutation.isPending}
                    className="gap-1.5 transition-colors duration-150"
                  >
                    {markInProgressMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    {t("solicitudes.acceptRequest")}
                  </Button>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("estadoFiscal.uploadBoletoDialogTitle")}</DialogTitle>
          </DialogHeader>
          <UploadBoletoForm
            obligationId={obligation.id}
            onSuccess={() => {
              setUploadDialogOpen(false);
              queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
              navigate("/requests");
            }}
            onCancel={() => setUploadDialogOpen(false)}
            uploadBoletoMutation={uploadBoletoMutation}
          />
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        title={t("estadoFiscal.ivaRemoveBoletoConfirmTitle")}
        description={t("estadoFiscal.ivaRemoveBoletoConfirmDescription")}
        confirmText={t("estadoFiscal.ivaRemoveBoleto")}
        variant="destructive"
        onConfirm={() => removeBoletoMutation.mutate(obligation.id)}
        isLoading={removeBoletoMutation.isPending}
      />
    </div>
  );
}
